import { useEffect, MutableRefObject } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { useUndoStore, pushToRedo, snapshotFromStore, captureSnapshot, restoreSnapshot } from '../stores/undoStore';
import { useAIStore } from '../stores/aiStore';
import { useUIStore } from '../stores/uiStore';
import { useSettingsStore } from '../stores/settingsStore';
import { edgeService } from '../services';
import { LAYER_PALETTE } from '../constants';
import { EDGE } from '../constants/edges';
import { NodeFactory } from '../services/node/NodeFactory';
import { CHORD_PREFIX_HINTS, CHORD_PREFIXES, NODE_CHORD_MAP, findSmartPosition } from '../utils/chords';
import { DRAW_CHORD_ACTIONS, DRAW_CHORD_HINT, DRAW_STICKY_CHORD_HINT } from '../utils/drawTools';
import { applyColorToSelection, colorPickerPayloadForSelection, colorPresetAt, withStickyDrawColor } from '../utils/colorPresets';
import { connectNodeChain } from '../utils/connectTools';
import { buildSocketsForDirection, calcNodeHeight } from '../utils/socketLayout';
import { isMarkupType, isMagneticNode } from '../constants/nodeTypes';
import type { SequenceMessage } from '../types';
import type { ContextMenuState } from '../types/app';
import { usedViaKeyboard, usedViaKeyboardNodeType } from '../onboarding';

function getSelectedMessageIds() {
  const st = useDiagramStore.getState();
  return st.selectedItems.filter(id => st.messages.some(message => message.id === id));
}

function increaseSelectedMessageThickness(messageIds: string[]) {
  const st = useDiagramStore.getState();
  messageIds.forEach(id => {
    const message = st.messages.find(item => item.id === id);
    if (!message) return;
    const current = message.strokeWidth ?? EDGE.DEFAULT_SEQUENCE_MESSAGE_STROKE_WIDTH;
    const next = EDGE.STROKE_STEPS.find(step => step > current) ?? EDGE.STROKE_STEPS[EDGE.STROKE_STEPS.length - 1];
    st.updateMessage(id, { strokeWidth: next });
  });
}

function decreaseSelectedMessageThickness(messageIds: string[]) {
  const st = useDiagramStore.getState();
  messageIds.forEach(id => {
    const message = st.messages.find(item => item.id === id);
    if (!message) return;
    const current = message.strokeWidth ?? EDGE.DEFAULT_SEQUENCE_MESSAGE_STROKE_WIDTH;
    const next = [...EDGE.STROKE_STEPS].reverse().find(step => step < current) ?? EDGE.STROKE_STEPS[0];
    st.updateMessage(id, { strokeWidth: next });
  });
}

function cycleSelectedMessageDashStyle(messageIds: string[]) {
  const st = useDiagramStore.getState();
  messageIds.forEach(id => {
    const message = st.messages.find(item => item.id === id);
    if (!message) return;
    const current = message.dashStyle ?? (message.type === 'return' ? 'dashed' : 'solid');
    const idx = EDGE.DASH_CYCLE.indexOf(current);
    const next = EDGE.DASH_CYCLE[(idx + 1) % EDGE.DASH_CYCLE.length];
    st.updateMessage(id, { dashStyle: next });
  });
}

function cycleSelectedMessageType(messageIds: string[]) {
  const st = useDiagramStore.getState();
  const typeCycle: SequenceMessage['type'][] = ['sync', 'async', 'return'];
  messageIds.forEach(id => {
    const message = st.messages.find(item => item.id === id);
    if (!message) return;
    const idx = typeCycle.indexOf(message.type);
    const next = typeCycle[(idx + 1) % typeCycle.length];
    st.updateMessage(id, { type: next });
  });
}

type ColorPickerState = {
  show: boolean;
  targetNodeIds: string[];
  targetEdgeIds?: string[];
  targetGroupIds?: string[];
  targetParticipantIds?: string[];
  targetMessageIds?: string[];
  avoidRect?: { x: number; y: number; w: number; h: number };
} | null;

type InlineEditState = { nodeId: string; field: 'label' | 'description' } | null;
type InlineSocketEditState = { nodeId: string; socketId: string } | null;
type TooltipState = { screenX: number; screenY: number; nodeId: string } | null;
type SequenceDragState = {
  sourceParticipantId: string;
  sourceX: number;
  startY: number;
  currentX?: number;
} | null;
type QuickNodeCreationState = { show: boolean; screenX: number; screenY: number } | null;
type FrameDrawRectState = { x: number; y: number; w: number; h: number } | null;

export interface AppKeyboardHandlers {
  // UI state setters
  setSpaceHeld: (v: boolean) => void;
  setChordHint: (v: string) => void;
  setIsFrameDrawMode: (v: boolean) => void;
  setCanvasDrawTool?: (v: import('../stores/uiStore').CanvasDrawTool) => void;
  setMarkupStroke?: (v: { x1: number; y1: number; x2: number; y2: number } | null) => void;
  setColorPicker: (v: ColorPickerState) => void;
  setIsMermaidModalOpen: (v: boolean) => void;
  setIsDevStateViewerOpen: (v: boolean) => void;
  setIsSettingsOpen: (v: boolean) => void;
  setInlineEdit: (v: InlineEditState) => void;
  setInlineSocketEdit: (v: InlineSocketEditState) => void;
  setTooltip: (v: TooltipState) => void;
  setContextMenu: (v: ContextMenuState) => void;
  setInfoPanelNodeId: (v: string | null) => void;
  setInfoPanelParticipantId: (v: string | null) => void;
  setInfoPanelMessageId: (v: string | null) => void;
  setSequenceDrag: (v: SequenceDragState) => void;
  setActiveFlowId: (v: string | null) => void;
  setQuickNodeCreation: (v: QuickNodeCreationState) => void;
  setFrameDrawRect: (v: FrameDrawRectState) => void;
  setAnchorNodeId: (v: string | null) => void;
  setDiscoveryMode: (v: boolean) => void;
  setSeqClipboard: (v: SequenceMessage | null) => void;
  setIsCut: (v: boolean) => void;
  infoPanelParticipantId: string | null;
  infoPanelMessageId: string | null;
  setSidebarTab: (v: 'palette' | 'info' | 'pages') => void;
  setIsToolboxOpen: (v: boolean) => void;
  showToast: (msg: string) => void;
  isDistractionFree: boolean;
  setIsDistractionFree: (v: boolean) => void;
  setIsChordModalOpen: (v: boolean) => void;

  // Refs
  chordBuffer: MutableRefObject<string>;
  chordTimer: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  rKeyHeld: MutableRefObject<boolean>;
  bKeyHeld: MutableRefObject<boolean>;
  mKeyHeld: MutableRefObject<boolean>;
  eKeyHeld: MutableRefObject<boolean>;
  frameDrawStart: MutableRefObject<{ x: number; y: number } | null>;
  discoveryTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  lastMousePos: MutableRefObject<{ x: number; y: number } | null>;
  lastNodeAddPos: MutableRefObject<{ x: number; y: number } | null>;
  lastChordAddCanvasPos: MutableRefObject<{ x: number; y: number } | null>;
  lastAddedNodeId: MutableRefObject<string | null>;
  pendingEditAfterAdd: MutableRefObject<boolean>;
  cursorCanvasPos: MutableRefObject<{ x: number; y: number }>;

  // Snapshot values needed for closures
  contextMenu: ContextMenuState;
  infoPanelNodeId: string | null;
  inlineEdit: InlineEditState;
  tooltip: TooltipState;
  connectionState: { isConnecting: boolean };
  selectedItems: string[];
  isSettingsOpen: boolean;
  isMermaidModalOpen: boolean;
  isDevStateViewerOpen: boolean;
  isFrameDrawMode: boolean;
  canvasDrawTool?: import('../stores/uiStore').CanvasDrawTool;
  canvasStampType?: string | null;
  setCanvasStampType?: (type: string | null) => void;
  colorPicker: ColorPickerState;
  inlineSocketEdit: InlineSocketEditState;
  sequenceDrag: SequenceDragState;
  activeFlowId: string | null;
  quickNodeCreation: QuickNodeCreationState;
  viewport: { x: number; y: number; scale: number };
  windowSize: { width: number; height: number };
  diagramType: string;
  seqClipboard: SequenceMessage | null;
  isCut: boolean;
  isReadOnly: boolean;

  // Coordinate helper
  toCanvas: (sx: number, sy: number) => { x: number; y: number };
}

export function useAppKeyboard(handlers: AppKeyboardHandlers) {
  const {
    setSpaceHeld, setChordHint, setIsFrameDrawMode, setCanvasDrawTool, setMarkupStroke, setColorPicker,
    setIsMermaidModalOpen, setIsDevStateViewerOpen, setIsSettingsOpen,
    setInlineEdit, setInlineSocketEdit, setTooltip, setContextMenu,
    setInfoPanelNodeId, setInfoPanelParticipantId, setInfoPanelMessageId,
    setSequenceDrag, setActiveFlowId, setQuickNodeCreation,
    setFrameDrawRect, setAnchorNodeId, setDiscoveryMode, setSeqClipboard,
    setIsCut, setSidebarTab, setIsToolboxOpen, showToast,
    isDistractionFree, setIsDistractionFree, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setIsChordModalOpen: _setIsChordModalOpen,
    chordBuffer, chordTimer, rKeyHeld, bKeyHeld, mKeyHeld, eKeyHeld, frameDrawStart,
    discoveryTimerRef, lastMousePos, lastNodeAddPos, lastChordAddCanvasPos,
    lastAddedNodeId, pendingEditAfterAdd, cursorCanvasPos,
    contextMenu, infoPanelNodeId, infoPanelParticipantId, infoPanelMessageId,
    inlineEdit, tooltip, connectionState,
    selectedItems, isSettingsOpen, isMermaidModalOpen, isDevStateViewerOpen,
    isFrameDrawMode, canvasDrawTool, canvasStampType, setCanvasStampType, colorPicker, inlineSocketEdit, sequenceDrag,
    activeFlowId, quickNodeCreation, viewport, windowSize,
    diagramType, seqClipboard, isCut, isReadOnly, toCanvas,
  } = handlers;

  const { undo, redo } = useUndoStore();

  useEffect(() => {
    let eTapTimer: ReturnType<typeof setTimeout> | null = null;

    const onDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable;

      // -- Undo (Ctrl+Z) - works even while typing --------------------------
      if (!isReadOnly && e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        usedViaKeyboard('undo');
        const snapshot = undo();
        if (snapshot) {
          pushToRedo(captureSnapshot());
          restoreSnapshot(snapshot);
        }
        return;
      }

      // -- Redo (Ctrl+Shift+Z) ----------------------------------------------
      if (!isReadOnly && e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        usedViaKeyboard('redo');
        const snapshot = redo();
        if (snapshot) {
          snapshotFromStore();
          restoreSnapshot(snapshot);
        }
        return;
      }

      // -- Toggle AI Chat (Ctrl+Shift+A) ------------------------------------
      if (e.key.toLowerCase() === 'a' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        usedViaKeyboard('ai-chat');
        useAIStore.getState().togglePanel();
        return;
      }

      // -- Command palette (Ctrl/Cmd+K) ------------------------------------
      if ((e.key === 'k' || e.key === 'K') && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        usedViaKeyboard('command-palette');
        const ui = useUIStore.getState();
        ui.setCommandPaletteOpen(!ui.isCommandPaletteOpen);
        return;
      }

      // -- I key: tap opens info; hold inspects packed info -----------------
      if ((e.key === 'i' || e.key === 'I') && !e.repeat && !e.ctrlKey && !e.metaKey && !isTyping) {
        if (!discoveryTimerRef.current) {
          discoveryTimerRef.current = setTimeout(() => {
            usedViaKeyboard('inspect');
            setDiscoveryMode(true);
            discoveryTimerRef.current = null;
          }, 280);
        }
        usedViaKeyboard('info-panel');
        if (isReadOnly) {
          setSidebarTab('info');
          setIsToolboxOpen(true);
          return;
        }
        const st = useDiagramStore.getState();
        const cp = cursorCanvasPos.current;
        if (st.diagramType === 'sequence') {
          // Check hovered participant (HEADER_Y=40, HEADER_HEIGHT=44, HEADER_WIDTH=140)
          const hovP = st.participants.find(p =>
            cp.x >= p.x && cp.x <= p.x + 140 && cp.y >= 40 && cp.y <= 84
          );
          // Check hovered/selected message
          const hovM = st.messages.find(m => Math.abs(cp.y - m.y) < 12);
          if (hovP) {
            setInfoPanelParticipantId(hovP.id);
          } else if (hovM) {
            setInfoPanelMessageId(hovM.id);
          } else {
            const selP = st.selectedItems.find(id => st.participants.some(p => p.id === id));
            const selM = st.selectedItems.find(id => st.messages.some(m => m.id === id));
            if (selP) setInfoPanelParticipantId(selP);
            else if (selM) setInfoPanelMessageId(selM);
          }
        } else {
          const hoveredNode = st.nodes.find(n =>
            cp.x >= n.position.x && cp.x <= n.position.x + n.size.width &&
            cp.y >= n.position.y && cp.y <= n.position.y + n.size.height
          );
          if (hoveredNode && hoveredNode.type !== 'group_input' && hoveredNode.type !== 'group_output') {
            setInfoPanelNodeId(hoveredNode.id);
            setIsToolboxOpen(true);
          } else {
            setIsToolboxOpen(true);
            setSidebarTab('info');
          }
        }
      }

      if (isTyping && e.key !== 'Escape') return;

      // -- Sequence copy / cut / paste --------------------------------------
      if (diagramType === 'sequence') {
        const st = useDiagramStore.getState();
        if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
          const selectedMsg = st.messages.find(m => st.selectedItems.includes(m.id));
          if (selectedMsg) { e.preventDefault(); usedViaKeyboard('copy'); setSeqClipboard({ ...selectedMsg }); setIsCut(false); }
          return;
        }
        if (!isReadOnly && e.key === 'x' && (e.ctrlKey || e.metaKey)) {
          const selectedMsg = st.messages.find(m => st.selectedItems.includes(m.id));
          if (selectedMsg) {
            e.preventDefault();
            usedViaKeyboard('cut');
            setSeqClipboard({ ...selectedMsg }); setIsCut(true);
            snapshotFromStore();
            st.removeMessage(selectedMsg.id);
            st.clearSelection();
          }
          return;
        }
        if (!isReadOnly && e.key === 'v' && (e.ctrlKey || e.metaKey) && seqClipboard) {
          e.preventDefault();
          usedViaKeyboard('paste');
          const pasteY = cursorCanvasPos.current.y;
          if (!st.participants.some(p => p.id === seqClipboard.sourceId) ||
              !st.participants.some(p => p.id === seqClipboard.targetId)) return;
          const targetFrag = st.fragments.find(f => pasteY > f.startY && pasteY < f.endY);
          snapshotFromStore();
          st.addMessage({
            sourceId: seqClipboard.sourceId, targetId: seqClipboard.targetId, y: pasteY,
            label: seqClipboard.label, type: seqClipboard.type, color: seqClipboard.color,
            strokeWidth: seqClipboard.strokeWidth, dashStyle: seqClipboard.dashStyle,
            description: seqClipboard.description, tooltip: seqClipboard.tooltip,
            payload: seqClipboard.payload, constraints: seqClipboard.constraints,
            links: seqClipboard.links ? seqClipboard.links.map(link => ({ ...link })) : undefined,
            fragmentId: targetFrag?.id,
          });
          if (isCut) { setSeqClipboard(null); setIsCut(false); }
          return;
        }
      }

      // -- Escape - dismiss UI layers in priority order ---------------------
      if (e.key === 'Escape') {
        if (useUIStore.getState().isCommandPaletteOpen) {
          useUIStore.getState().setCommandPaletteOpen(false);
          return;
        }
        if (chordBuffer.current) {
          chordBuffer.current = '';
          if (chordTimer.current) clearTimeout(chordTimer.current);
          setChordHint(''); return;
        }
        if (activeFlowId) { setActiveFlowId(null); return; }
        if (quickNodeCreation?.show) { useDiagramStore.getState().cancelConnection(); setQuickNodeCreation(null); return; }
        if (isFrameDrawMode || (canvasDrawTool && canvasDrawTool !== 'none') || canvasStampType || useUIStore.getState().canvasConnectMode !== 'none') {
          useUIStore.getState().clearArmedTools();
          setIsFrameDrawMode(false);
          setCanvasDrawTool?.('none');
          setCanvasStampType?.(null);
          frameDrawStart.current = null;
          setFrameDrawRect(null);
          setMarkupStroke?.(null);
          return;
        }
        if (colorPicker?.show) { setColorPicker(null); return; }
        if (isMermaidModalOpen) { setIsMermaidModalOpen(false); return; }
        if (isDevStateViewerOpen) { setIsDevStateViewerOpen(false); return; }
        if (isSettingsOpen) { setIsSettingsOpen(false); return; }
        if (inlineEdit) { setInlineEdit(null); return; }
        if (inlineSocketEdit) { setInlineSocketEdit(null); return; }
        if (tooltip) { setTooltip(null); return; }
        if (contextMenu) { setContextMenu(null); return; }
        if (infoPanelNodeId) { setInfoPanelNodeId(null); return; }
        if (infoPanelParticipantId) { setInfoPanelParticipantId(null); return; }
        if (infoPanelMessageId) { setInfoPanelMessageId(null); return; }
        if (sequenceDrag) { setSequenceDrag(null); return; }
        const st = useDiagramStore.getState();
        if (connectionState.isConnecting) st.cancelConnection();
        else { st.clearSelection(); setAnchorNodeId(null); lastAddedNodeId.current = null; lastChordAddCanvasPos.current = null; }
        return;
      }

      // -- Delete / Backspace -----------------------------------------------
      const activeTag2 = (document.activeElement?.tagName ?? '').toLowerCase();
      const isTypingNow = activeTag2 === 'input' || activeTag2 === 'textarea' ||
        (document.activeElement as HTMLElement)?.isContentEditable;
      if (!isReadOnly && (e.key === 'Delete' || e.key === 'Backspace') && !connectionState.isConnecting && !isTypingNow) {
        const st = useDiagramStore.getState();
        if (selectedItems.length > 0) {
          usedViaKeyboard('delete-selection');
          snapshotFromStore();
        }
        selectedItems.forEach(id => {
          if (st.edges.some(edge => edge.id === id)) st.removeEdge(id);
          else if (st.nodes.some(n => n.id === id)) st.removeNode(id);
          else if (st.messages.some(m => m.id === id)) st.removeMessage(id);
          else if (st.participants.some(p => p.id === id)) st.removeParticipant(id);
          else if (st.fragments.some(f => f.id === id)) st.removeFragment(id);
          else if (st.participantGroups.some(g => g.id === id)) st.ungroupParticipants(id);
          else if (st.groups.some(g => g.id === id)) st.ungroupNodes(id);
        });
      }

      // -- X key: sever edges from selected nodes --------------------------
      if (!isReadOnly && e.key === 'x' && !e.ctrlKey && !e.metaKey && !inlineEdit && !connectionState.isConnecting) {
        const st = useDiagramStore.getState();
        const selectedNodeIds = selectedItems.filter(id => st.nodes.some(n => n.id === id));
        if (selectedNodeIds.length > 0) {
          const edgesToRemove = st.edges.filter(edge =>
            selectedNodeIds.includes(edge.sourceNodeId) || selectedNodeIds.includes(edge.targetNodeId)
          );
          if (edgesToRemove.length > 0) {
            snapshotFromStore();
            edgesToRemove.forEach(edge => st.removeEdge(edge.id));
          }
        }
      }

      // -- Edge style shortcuts ---------------------------------------------
      if (!isReadOnly && !isTyping && !connectionState.isConnecting) {
        const selEdgeIds = edgeService.getSelectedEdgeIds();
        if (selEdgeIds.length > 0) {
          if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
            e.preventDefault(); snapshotFromStore(); edgeService.increaseThickness(selEdgeIds);
          }
          if ((e.ctrlKey || e.metaKey) && e.key === '-') {
            e.preventDefault(); snapshotFromStore(); edgeService.decreaseThickness(selEdgeIds);
          }
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.key === '-') {
            e.preventDefault(); snapshotFromStore(); edgeService.cycleDashStyle(selEdgeIds);
          }
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.key === '=') {
            e.preventDefault(); snapshotFromStore(); edgeService.cycleEdgeType(selEdgeIds);
          }
        }

        const selMessageIds = diagramType === 'sequence' ? getSelectedMessageIds() : [];
        if (selMessageIds.length > 0) {
          if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
            e.preventDefault(); snapshotFromStore(); increaseSelectedMessageThickness(selMessageIds);
          }
          if ((e.ctrlKey || e.metaKey) && e.key === '-') {
            e.preventDefault(); snapshotFromStore(); decreaseSelectedMessageThickness(selMessageIds);
          }
          if (!e.ctrlKey && !e.metaKey && (e.key === '-' || e.key === '_')) {
            e.preventDefault(); snapshotFromStore(); cycleSelectedMessageDashStyle(selMessageIds);
          }
          if (!e.ctrlKey && !e.metaKey && (e.key === '=' || e.key === '+')) {
            e.preventDefault(); snapshotFromStore(); cycleSelectedMessageType(selMessageIds);
          }
        }
      }

      // -- Ctrl+G: group selected nodes -------------------------------------
      if (!isReadOnly && (e.ctrlKey || e.metaKey) && e.key === 'g' && diagramType === 'flow') {
        e.preventDefault();
        const st = useDiagramStore.getState();
        const nodeIds = selectedItems.filter(id => { const n = st.nodes.find(nd => nd.id === id); return n && !n.childGraphId; });
        if (nodeIds.length >= 2) { usedViaKeyboard('group-nodes'); snapshotFromStore(); st.createGroup(nodeIds); }
      }

      // -- R key hold -------------------------------------------------------
      if (!isReadOnly && (e.key === 'r' || e.key === 'R') && !e.repeat && !e.ctrlKey && !e.metaKey && !isTyping) {
        rKeyHeld.current = true;
      }

      // -- M key hold: edge-move mode ---------------------------------------
      if (!isReadOnly && (e.key === 'm' || e.key === 'M') && !e.repeat && !e.ctrlKey && !e.metaKey && !isTyping) {
        mKeyHeld.current = true;
      }

      // -- Space = pan ------------------------------------------------------
      if (e.key === ' ' && !e.repeat) { e.preventDefault(); setSpaceHeld(true); }


      // -- Tab: enter / exit group (Blender-style) --------------------------
      if (e.key === 'Tab' && !e.repeat && diagramType === 'flow') {
        e.preventDefault();
        usedViaKeyboard('enter-group');
        const st = useDiagramStore.getState();
        if (st.graphNavStack.length > 0) {
          st.exitGraph();
        } else {
          const selectedGroupNode = st.nodes.find(n => selectedItems.includes(n.id) && n.childGraphId);
          if (selectedGroupNode) st.enterGraph(selectedGroupNode.id);
        }
      }

      // -- E: hold = connect from body; tap = connect selection or arm click-to-connect
      if (!isReadOnly && (e.key === 'e' || e.key === 'E') && !e.ctrlKey && !e.metaKey && !e.altKey && !isTyping && !chordBuffer.current && diagramType === 'flow') {
        eKeyHeld.current = true;
        if (!e.repeat && eTapTimer === null) {
          eTapTimer = setTimeout(() => { eTapTimer = null; }, 220);
        }
      }

      // -- B key hold: frame draw mode --------------------------------------
      if (!isReadOnly && (e.key === 'b' || e.key === 'B') && !e.repeat && !e.ctrlKey && !e.metaKey && diagramType !== 'sequence' && !chordBuffer.current) {
        e.preventDefault(); bKeyHeld.current = true; usedViaKeyboard('draw-frame'); setIsFrameDrawMode(true); return;
      }

      // -- 2: cycle box draw tool (once → sticky → off) ---------------------
      if (!isReadOnly && e.key === '2' && !e.ctrlKey && !e.metaKey && !e.altKey && diagramType !== 'sequence' && !chordBuffer.current) {
        e.preventDefault();
        usedViaKeyboard('draw-box');
        useUIStore.getState().cycleDrawTool('box');
        bKeyHeld.current = false;
        frameDrawStart.current = null;
        setFrameDrawRect(null);
        setMarkupStroke?.(null);
        return;
      }

      // -- Chord macros -----------------------------------------------------
      if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) {
        const k = e.key.toLowerCase();

        if (chordBuffer.current) {
          if (chordTimer.current) clearTimeout(chordTimer.current);
          const chord = `${chordBuffer.current}+${k}`;
          chordBuffer.current = '';
          setChordHint('');

          if (activeFlowId) { e.preventDefault(); return; }
          if (isReadOnly) {
            if (chord === 't+p') { setSidebarTab('pages'); setIsToolboxOpen(true); e.preventDefault(); return; }
            if (chord === 't+i') { setSidebarTab('info'); setIsToolboxOpen(true); e.preventDefault(); return; }
            if (chord === 't+g') { setSidebarTab('pages'); setIsToolboxOpen(true); e.preventDefault(); return; }
            if (chord === 't+f') { setIsDistractionFree(!isDistractionFree); e.preventDefault(); return; }
            e.preventDefault();
            return;
          }

          // In sequence mode, a+p adds a participant instead of a flow node
          if (chord === 'a+p' && diagramType === 'sequence') {
            const st = useDiagramStore.getState();
            snapshotFromStore();
            usedViaKeyboard('add-participant');
            st.addParticipant({ label: `Participant ${st.participants.length + 1}` });
            e.preventDefault();
          }
          if (chord === 'a+t' && diagramType !== 'sequence') {
            usedViaKeyboard('add-text');
            useUIStore.getState().activateDrawTool('text', false);
            e.preventDefault();
            return;
          }
          const nodeType = NODE_CHORD_MAP[chord];
          if (nodeType && diagramType !== 'sequence') {
            _handleChordNodeAdd(nodeType, { e, viewport, windowSize, toCanvas, lastMousePos, lastChordAddCanvasPos, lastNodeAddPos, lastAddedNodeId, pendingEditAfterAdd, setAnchorNodeId });
          } else if ((chord === 'n+i' || chord === 'n+o') && diagramType === 'flow') {
            const st = useDiagramStore.getState();
            const selectedNodeIds = st.selectedItems.filter(id => st.nodes.some(n => n.id === id));
            if (selectedNodeIds.length !== 1) {
              showToast('Select a single node, then press n+i or n+o');
              e.preventDefault();
              return;
            }

            const nodeId = selectedNodeIds[0];
            const node = st.nodes.find(n => n.id === nodeId);
            if (!node) {
              e.preventDefault();
              return;
            }
            if (isMarkupType(node.type)) {
              showToast('Drawing annotations do not have sockets');
              e.preventDefault();
              return;
            }

            const direction: 'input' | 'output' = chord === 'n+i' ? 'input' : 'output';
            if (isMagneticNode(node.type)) {
              showToast('Drawing shapes do not support custom sockets');
              e.preventDefault();
              return;
            }
            if (node.type === 'group_input' && direction === 'input') {
              showToast('Group Input nodes only allow output sockets');
              e.preventDefault();
              return;
            }
            if (node.type === 'group_output' && direction === 'output') {
              showToast('Group Output nodes only allow input sockets');
              e.preventDefault();
              return;
            }

            snapshotFromStore();
            usedViaKeyboard(direction === 'input' ? 'add-socket-in' : 'add-socket-out');
            const socketId = st.addSocket(nodeId, direction);
            if (socketId) {
              setInlineSocketEdit({ nodeId, socketId });
            } else {
              showToast('Unable to add socket on selected node');
            }
            e.preventDefault();
          } else if (chord === 'r+n' && diagramType === 'flow') {
            const st = useDiagramStore.getState();
            const selectedNodeIds = st.selectedItems.filter(id => st.nodes.some(n => n.id === id));
            if (selectedNodeIds.length === 0) {
              showToast('Select a node, then press r+n to rotate direction');
              e.preventDefault();
              return;
            }
            const SHAPED_TYPES_SET = new Set(['diamond', 'database', 'queue', 'waypoint', 'group_input', 'group_output']);
            snapshotFromStore();
            usedViaKeyboard('rotate-direction');
            let lastDir = 'ltr';
            for (const nodeId of selectedNodeIds) {
              const node = st.nodes.find(n => n.id === nodeId);
              if (!node || SHAPED_TYPES_SET.has(node.type) || isMagneticNode(node.type)) continue;
              const currentDir = (node.data?.direction as 'ltr' | 'ttb' | 'rtl') ?? 'ltr';
              const cycle: Array<'ltr' | 'ttb' | 'rtl'> = ['ltr', 'ttb', 'rtl'];
              const nextDir = cycle[(cycle.indexOf(currentDir) + 1) % cycle.length];
              const nIn = node.sockets.filter(s => s.type === 'input').length;
              const nOut = node.sockets.filter(s => s.type === 'output').length;
              const nodeH = calcNodeHeight(nIn, nOut);
              const sockets = buildSocketsForDirection(node.type, node.size.width, nodeH, nextDir);
              const newSockets = sockets.map(ns => {
                const old = node.sockets.find(os => os.id === ns.id);
                return old ? { ...ns, label: old.label } : ns;
              });
              st.updateNode(nodeId, { sockets: newSockets, data: { ...node.data, direction: nextDir } });
              if (node.parentGroupId) st.recalcGroupBounds(node.parentGroupId);
              lastDir = nextDir;
            }
            showToast(`Rotated to ${lastDir.toUpperCase()}`);
            e.preventDefault();
          } else if (chord === 'l+n') {
            const st = useDiagramStore.getState();
            snapshotFromStore();
            usedViaKeyboard('add-layer');
            st.addLayer(`Layer ${st.layers.length + 1}`, LAYER_PALETTE[st.layers.length % LAYER_PALETTE.length]);
            e.preventDefault();
          } else if (chord === 'l+d') {
            const st = useDiagramStore.getState();
            const al = st.layers.find(l => l.id === st.activeLayerId);
            if (al) { snapshotFromStore(); usedViaKeyboard('dim-layer'); st.updateLayer(al.id, { dimmed: !al.dimmed }); }
            e.preventDefault();
          } else if (chord === 'l+h') {
            const st = useDiagramStore.getState();
            const al = st.layers.find(l => l.id === st.activeLayerId);
            if (al) { snapshotFromStore(); usedViaKeyboard('hide-layer'); st.updateLayer(al.id, { visible: !al.visible }); }
            e.preventDefault();
          } else if (chord === 'c+c') {
            usedViaKeyboard('change-color');
            chordBuffer.current = 'c+c';
            setChordHint('c+c ... 0-9 preset color  c=picker');
            chordTimer.current = setTimeout(() => {
              chordBuffer.current = '';
              setChordHint('');
              const result = colorPickerPayloadForSelection(viewport, cursorCanvasPos.current);
              if ('picker' in result) setColorPicker(result.picker);
              else showToast(result.toast);
            }, 1500);
            e.preventDefault();
          } else if (/^c\+c\+[0-9]$/.test(chord)) {
            const digit = Number(chord.slice(-1));
            const preset = colorPresetAt(digit);
            if (preset) {
              const painted = applyColorToSelection(preset.hex);
              const extra = useSettingsStore.getState().stickyDrawColorEnabled ? ' · new shapes will use this' : '';
              showToast(painted > 0 ? `${preset.name}${extra}` : `${preset.name} saved${extra || ' · turn on sticky draw color in Settings'}`);
            }
            e.preventDefault();
          } else if (chord === 'c+c+c') {
            usedViaKeyboard('change-color');
            const result = colorPickerPayloadForSelection(viewport, cursorCanvasPos.current);
            if ('picker' in result) setColorPicker(result.picker);
            else showToast(result.toast);
            e.preventDefault();
          } else if ((chord === 'c++' || chord === 'c+=' || chord === 'c+-') && diagramType === 'flow') {
            const st = useDiagramStore.getState();
            const selEdgeIds = st.selectedItems.filter(id => st.edges.some(ed => ed.id === id));
            if (selEdgeIds.length === 0) {
              showToast('Select edges first, then c++ / c+- to adjust curve strength');
              e.preventDefault();
            } else {
              const delta = (chord === 'c++' || chord === 'c+=') ? 0.25 : -0.25;
              snapshotFromStore();
              let lastStrength = 1.0;
              for (const edgeId of selEdgeIds) {
                const edge = st.edges.find(ed => ed.id === edgeId);
                if (!edge) continue;
                const current = edge.curveStrength ?? 1.0;
                const next = Math.max(0.1, Math.min(4.0, Math.round((current + delta) * 100) / 100));
                st.updateEdge(edgeId, { curveStrength: next });
                lastStrength = next;
              }
              showToast(`Curve strength: ${lastStrength.toFixed(2)}x`);
              e.preventDefault();
            }
          } else if (chord === 't+p') { usedViaKeyboard('sidebar-palette'); setSidebarTab('palette'); setIsToolboxOpen(true); e.preventDefault(); }
            else if (chord === 't+i') { usedViaKeyboard('sidebar-info'); setSidebarTab('info'); setIsToolboxOpen(true); e.preventDefault(); }
            else if (chord === 't+g') { usedViaKeyboard('sidebar-pages'); setSidebarTab('pages'); setIsToolboxOpen(true); e.preventDefault(); }
            else if (chord === 't+a') {
            const selEdgeIds = edgeService.getSelectedEdgeIds();
            if (selEdgeIds.length > 0) { snapshotFromStore(); usedViaKeyboard('toggle-animation'); edgeService.toggleAnimation(selEdgeIds); }
            else showToast('Select edges first, then press t+a to toggle animation');
            e.preventDefault();
          } else if (chord === 't+e') {
            chordBuffer.current = 't+e';
            setChordHint(CHORD_PREFIX_HINTS['t+e']);
            chordTimer.current = setTimeout(() => { chordBuffer.current = ''; setChordHint(''); }, 1500);
            e.preventDefault();
          } else if (chord === 't+e+a') {
            snapshotFromStore(); edgeService.toggleAllAnimation(); e.preventDefault();
          } else if (chord === 'd+d') {
            chordBuffer.current = 'd+d';
            setChordHint(DRAW_STICKY_CHORD_HINT);
            chordTimer.current = setTimeout(() => { chordBuffer.current = ''; setChordHint(''); }, 1500);
            e.preventDefault();
          } else if (chord.startsWith('d+d+') || (chord.startsWith('d+') && chord !== 'd+d')) {
            const key = chord.startsWith('d+d+') ? chord.slice(4) : chord.slice(2);
            const sticky = chord.startsWith('d+d+');
            const action = DRAW_CHORD_ACTIONS[key];
            if (action && diagramType !== 'sequence') {
              if (action.kind === 'tool') {
                if (action.tool === 'box') usedViaKeyboard('draw-box');
                if (action.tool === 'text') usedViaKeyboard('add-text');
                if (action.tool === 'frame') usedViaKeyboard('draw-frame');
                useUIStore.getState().activateDrawTool(action.tool, sticky);
              } else {
                useUIStore.getState().activateStampType(action.type, sticky);
              }
            } else {
              showToast(sticky ? DRAW_STICKY_CHORD_HINT : DRAW_CHORD_HINT);
            }
            e.preventDefault();
          } else if (chord === 't+f') {
            usedViaKeyboard('distraction-free');
            setIsDistractionFree(!isDistractionFree);
            e.preventDefault();
          }
        } else if (CHORD_PREFIXES.has(k) && (!isReadOnly || k === 't')) {
          if (activeFlowId && k !== 'a') { e.preventDefault(); return; }
          chordBuffer.current = k;
          setChordHint(activeFlowId ? 'a -> n: add note' : (k === 'd' ? DRAW_CHORD_HINT : CHORD_PREFIX_HINTS[k]));
          chordTimer.current = setTimeout(() => { chordBuffer.current = ''; setChordHint(''); }, 1500);
          e.preventDefault();
        }
      }
    };

    const onUp = (e: KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R') rKeyHeld.current = false;
      if (e.key === 'm' || e.key === 'M') mKeyHeld.current = false;
      if (e.key === 'e' || e.key === 'E') {
        eKeyHeld.current = false;
        if (eTapTimer) {
          clearTimeout(eTapTimer);
          eTapTimer = null;
          const tag = (e.target as HTMLElement).tagName;
          const typing = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable;
          if (!typing && diagramType === 'flow' && !isReadOnly) {
            const st = useDiagramStore.getState();
            const selectedNodeIds = st.selectedItems.filter(id => st.nodes.some(n => n.id === id && !isMarkupType(n.type)));
            const ui = useUIStore.getState();
            if (selectedNodeIds.length >= 2 && ui.canvasConnectMode === 'none') {
              const created = connectNodeChain(selectedNodeIds);
              showToast(created > 0 ? `Connected ${created} link${created === 1 ? '' : 's'}` : 'Could not connect selection');
            } else {
              ui.cycleConnectMode();
              const mode = useUIStore.getState().canvasConnectMode;
              if (mode === 'once') showToast('Click two nodes to connect · tap E again to keep chaining');
              else if (mode === 'sticky') showToast('Connect sticky — click nodes to chain');
              else showToast('Connect off');
            }
          }
        }
      }
      if (e.key === ' ') setSpaceHeld(false);
      if ((e.key === 'b' || e.key === 'B') && bKeyHeld.current) {
        bKeyHeld.current = false; setIsFrameDrawMode(false); setCanvasDrawTool?.('none'); frameDrawStart.current = null; setFrameDrawRect(null); setMarkupStroke?.(null);
      }
      if (e.key === 'i' || e.key === 'I') {
        if (discoveryTimerRef.current) { clearTimeout(discoveryTimerRef.current); discoveryTimerRef.current = null; }
        setDiscoveryMode(false);
      }
    };

    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, [
    // All handler closures deps
    contextMenu, infoPanelNodeId, inlineEdit, tooltip, connectionState.isConnecting,
    selectedItems, isSettingsOpen, isMermaidModalOpen, isDevStateViewerOpen,
    isFrameDrawMode, canvasDrawTool, canvasStampType, colorPicker, inlineSocketEdit, sequenceDrag, activeFlowId,
    quickNodeCreation, viewport, windowSize, diagramType, seqClipboard, isCut, isReadOnly,
    // Stable setters - listed for exhaustive-deps
    setSpaceHeld, setChordHint, setIsFrameDrawMode, setColorPicker, setIsMermaidModalOpen,
    setIsDevStateViewerOpen, setIsSettingsOpen, setInlineEdit, setInlineSocketEdit,
    setTooltip, setContextMenu, setInfoPanelNodeId, setSequenceDrag, setActiveFlowId,
    setQuickNodeCreation, setFrameDrawRect, setAnchorNodeId, setDiscoveryMode,
    setSeqClipboard, setIsCut, setSidebarTab, setIsToolboxOpen, showToast, toCanvas, undo, redo,
    setInfoPanelParticipantId, setInfoPanelMessageId, setCanvasDrawTool, setMarkupStroke,
  ]);
}

// -- Private: chord-triggered node creation --------------------------------
// Extracted to keep the main effect readable; not exported.
function _handleChordNodeAdd(
  nodeType: string,
  ctx: {
    e: KeyboardEvent;
    viewport: { x: number; y: number; scale: number };
    windowSize: { width: number; height: number };
    toCanvas: (sx: number, sy: number) => { x: number; y: number };
    lastMousePos: MutableRefObject<{ x: number; y: number } | null>;
    lastChordAddCanvasPos: MutableRefObject<{ x: number; y: number } | null>;
    lastNodeAddPos: MutableRefObject<{ x: number; y: number } | null>;
    lastAddedNodeId: MutableRefObject<string | null>;
    pendingEditAfterAdd: MutableRefObject<boolean>;
    setAnchorNodeId: (id: string | null) => void;
  }
) {
  const { e, viewport, windowSize, toCanvas, lastMousePos, lastChordAddCanvasPos, lastNodeAddPos, lastAddedNodeId, pendingEditAfterAdd, setAnchorNodeId } = ctx;
  const st = useDiagramStore.getState();
  const sameTypeCount = st.nodes.filter(n => n.type === nodeType).length;
  const blueprint = NodeFactory.create(nodeType, sameTypeCount);
  const { size } = blueprint;

  const anchorId = (st.selectedItems.length === 1 && st.nodes.some(n => n.id === st.selectedItems[0]))
    ? st.selectedItems[0]
    : lastAddedNodeId.current ?? null;
  const anchor = anchorId ? st.nodes.find(n => n.id === anchorId) : null;

  const mouseOverCanvas = lastMousePos.current && lastMousePos.current.x > 250;
  let useMousePos = false;
  if (mouseOverCanvas && lastMousePos.current) {
    const mcp = toCanvas(lastMousePos.current.x, lastMousePos.current.y);
    const isOverNode = st.nodes.some(n =>
      mcp.x >= n.position.x - 20 && mcp.x <= n.position.x + n.size.width + 20 &&
      mcp.y >= n.position.y - 20 && mcp.y <= n.position.y + n.size.height + 20
    );
    if (lastChordAddCanvasPos.current) {
      const dx = mcp.x - lastChordAddCanvasPos.current.x;
      const dy = mcp.y - lastChordAddCanvasPos.current.y;
      useMousePos = (dx * dx + dy * dy > 10000) && !isOverNode;
    } else if (anchor) {
      const dx = mcp.x - anchor.position.x;
      const dy = mcp.y - anchor.position.y;
      useMousePos = (dx * dx + dy * dy > 10000) && !isOverNode;
    } else {
      useMousePos = !isOverNode;
    }
  }

  let pos: { x: number; y: number };
  if (useMousePos && lastMousePos.current) {
    const mcp = toCanvas(lastMousePos.current.x, lastMousePos.current.y);
    pos = { x: mcp.x - size.width / 2, y: mcp.y - size.height / 2 };
  } else if (anchor) {
    pos = findSmartPosition(anchor, size, st.nodes, st.groups);
  } else if (lastChordAddCanvasPos.current) {
    const lastNode = st.nodes[st.nodes.length - 1];
    pos = lastNode
      ? findSmartPosition(lastNode, size, st.nodes, st.groups)
      : { x: lastChordAddCanvasPos.current.x + 60, y: lastChordAddCanvasPos.current.y };
  } else {
    pos = {
      x: (windowSize.width / 2 - viewport.x) / viewport.scale - size.width / 2,
      y: (windowSize.height / 2 - viewport.y) / viewport.scale - size.height / 2,
    };
  }

  snapshotFromStore();
  usedViaKeyboardNodeType(nodeType);
  const activeGrpId = st.groupNavStack.length > 0 ? st.groupNavStack[st.groupNavStack.length - 1].groupId : undefined;
  if (!blueprint.skipAutoEdit) pendingEditAfterAdd.current = true;
  st.addNode({
    type: nodeType, position: pos, size: blueprint.size,
    label: blueprint.label,
    sockets: blueprint.sockets,
    data: withStickyDrawColor(blueprint.data),
    parentGroupId: activeGrpId,
  });

  const updatedNodes = useDiagramStore.getState().nodes;
  const newNodeId = updatedNodes[updatedNodes.length - 1]?.id ?? null;
  lastAddedNodeId.current = newNodeId;
  if (newNodeId) setAnchorNodeId(newNodeId);
  if (activeGrpId && newNodeId) st.addNodeToGroup(newNodeId, activeGrpId);
  if (lastMousePos.current) lastNodeAddPos.current = { ...lastMousePos.current };
  const finalNode = useDiagramStore.getState().nodes[useDiagramStore.getState().nodes.length - 1];
  if (finalNode) lastChordAddCanvasPos.current = { x: finalNode.position.x, y: finalNode.position.y };

  if (anchor && newNodeId) {
    const anchorOutputSocket = anchor.sockets.find(s => s.type === 'output');
    const newNodeInputSocket = updatedNodes[updatedNodes.length - 1]?.sockets.find(s => s.type === 'input');
    if (anchorOutputSocket && newNodeInputSocket) {
      st.addEdge({
        sourceNodeId: anchor.id, sourceSocketId: anchorOutputSocket.id,
        targetNodeId: newNodeId, targetSocketId: newNodeInputSocket.id,
      });
    }
  }
  e.preventDefault();
}
